import React, { useState, useEffect } from 'react';
import './edittask.css';
import moment from "moment";
import { toast } from 'react-toastify';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import TaskForm from './TaskForm';
import axiosInstance from '../../services/AxiosInstance';
import { FiArrowLeft } from 'react-icons/fi';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import { formatDateForInput, formatDateToDDMMMYYYY } from '../../utils/dateFormatter';
import { navigateBackSafely as safeNavigateBack } from '../../utils/navigateBackSafely';
import { withListQuery } from '../../utils/listQueryPersistence';
import {
  extractTimeFromDatetime,
  buildTaskDatetime,
  taskUsesSameDayTimes,
  toDateOnlyString,
} from '../../utils/taskTime';
import { useCanAccessProjects } from '../../hooks/useCanAccessProjects';

let userlist = [];

const EditTask = () => {
  const location = useLocation();
  const { taskid } = location.state || {};
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { canAccessProjects } = useCanAccessProjects();

  const [loading, setLoading] = useState(true);
  const [availableAssignees, setAvailableAssignees] = useState([]);
  const [Tags, setTags] = useState([]);
  const [observers, setObservers] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [projects, setProjects] = useState([]);
  const [userdata, setuserdata] = useState();
  const [Tasks, setTasks] = useState([]);
  const [Mentions, setMentions] = useState([]);
  const [delta, setDelta] = useState();
  const [Description, setDescription] = useState();
  const [initialData, setInitialData] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  // Repeat task context: 'series' = editing root (entire series), 'instance' = editing one instance
  const [repeatTaskContext, setRepeatTaskContext] = useState(null);

  // Helper function to safely navigate back, avoiding login page
  const navigateBackSafely = () => {
    safeNavigateBack(navigate, withListQuery('/task/tasklist', location.search));
  };

  const statusMap = {
    'To Do': 0,
    'In Progress': 1,
    'Postponed': 2,
    'Done': 3,
  };

  const returnStatusMap = {
    0: 'To Do',
    1: 'In Progress',
    2: 'Postponed',
    3: 'Done',
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem("user"));

        // Fetch users for assignees, observers, and mentions
        if (userData?.corporateId) {
          const params = new URLSearchParams();
          params.append('corporateId', parseInt(userData.corporateId));
          
          // Add branch and department parameters if available
          if (userData.branchId) {
            params.append('branch', userData.branchId);
          }
          if (userData.departmentId) {
            params.append('department', userData.departmentId);
          }

          const response = await axiosInstance.get(`/list/corporateuserslist?${params.toString()}`);
          const users = response.data.users || [];

          // Filter out the currently logged-in user
          const filteredUsers = users.filter(user => user.id !== userData.id);

          setAvailableAssignees(filteredUsers);
          setTags(filteredUsers);
          setParticipants(filteredUsers);
          setObservers(filteredUsers);
          userlist = filteredUsers;
        }

        // Fetch tasks
        const taskResponse = await axiosInstance.get('/task/list?');
        const taskData = taskResponse.data;
        setTasks(taskData.tasks || []);

      } catch (error) {
        console.error('Error fetching initial data:', error);
        
        // Handle 403 Forbidden error
        if (error.response && error.response.status === 409) {
          toast.error("Access denied. You don't have permission to access this data.");
          // Navigate back to task list after a short delay
          setTimeout(() => {
            navigate('/task/tasklist');
          }, 2000);
          return;
        }
        
        toast.error('Failed to load initial data');
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    setuserdata(JSON.parse(localStorage.getItem("user")));
    fetchData();
    fetchTaskDetails();
    fetchBranches();
    fetchDepartments();
    fetchSubDepartments();
  }, []);

  const fetchData = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      const corporateId = userData.corporateId;

      const queryParams = new URLSearchParams();
        
      queryParams.append('corporateId', corporateId);
      const response = await axiosInstance.get(`/task/list?${queryParams.toString()}`);
      const result = response.data;
      const formattedTasks = result.tasks.map(task => ({
        ...task,
        CreatedAt: formatDateWithSuffix(task.CreatedAt),
        StartDate: formatDateWithSuffix(task.StartDate),
        EndDate: formatDateWithSuffix(task.EndDate)
      }));
      setTasks(formattedTasks);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (!userData?.corporateId) return;

      const queryParams = new URLSearchParams();
      queryParams.append('corporateId', userData.corporateId);
      const response = await axiosInstance.get(`/project/list?${queryParams.toString()}`);
      setProjects(response.data?.data || []);
    } catch (error) {
      const status = error.response?.status;
      if (status === 403 || status === 409) return;
      console.error('Error fetching projects:', error);
    }
  };

  useEffect(() => {
    if (!canAccessProjects) return;
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessProjects]);

  const fetchBranches = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      
      const res = await axiosInstance.get(`/settings/branches?corporate_id=${corporateId}`);
      const data = res.data;
      
      if (res) {
        setBranches(data.data || data || []);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      toast.error('Failed to load branches');
    }
  };

  const fetchDepartments = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      
      const res = await axiosInstance.get(`/settings/departments?corporate_id=${corporateId}`);
      const data = res.data;
      
      if (res) {
        setDepartments(data.data || data || []);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      toast.error('Failed to load departments');
    }
  };

  const fetchSubDepartments = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      let res;
      try {
        res = await axiosInstance.get(`/settings/subDepartments?corporate_id=${corporateId}`);
      } catch (e1) {
        res = await axiosInstance.get(`/settings/subdepartments?corporate_id=${corporateId}`);
      }
      const data = res.data;
      if (res) {
        setSubDepartments(data.data || data || []);
      }
    } catch (error) {
      console.error('Error fetching sub-departments:', error);
      toast.error('Failed to load sub-departments');
    }
  };

  const fetchTaskDetails = async () => {
    try {
      const taskRes = await axiosInstance.get(`/task/taskdetails/${taskid}`);
      const taskData = taskRes.data;

      const formattedRepeatUntil = moment(taskData.repeat_until).format('YYYY-MM-DD');

      const enrichedMentionList = (taskData.Mentions || []).map(mention => {
        const matchedUser = (userlist || []).find(user => user.user_id === mention.id);
        return {
          ...mention,
          FullName: matchedUser?.full_name || '',
          Email: matchedUser?.email || '',
        };
      });

      const updatedChecklist = taskData.Checklists.map((item, index) => ({
        TaskChecklistId: item?.id || null,
        TaskId: item.task_id,
        value: item.content,
        checked: item.is_checked ? true : false,
        CreatedAt: new Date().toISOString(),
        id: Date.now() + Math.random()
      }));

      setDelta(taskData.delta);
      setDescription(taskData.description);
      setMentions(enrichedMentionList);

      const formattedData = {
        ...taskData,
        TaskName: taskData.title,
        StartDate: toDateOnlyString(taskData.start_date),
        Deadline: toDateOnlyString(taskData.due_date),
        StartTime: extractTimeFromDatetime(taskData.start_date),
        EndTime: extractTimeFromDatetime(taskData.due_date),
        Description: taskData.description,
        reminderinternalmessage: taskData.ReminderInternal,
        MarkHighPriority: taskData.IsHighPriority,
        TaskPin: taskData.PinTask,
        RepeatUntil: formattedRepeatUntil,
        RepeatType: taskData.repeate_type,
        RepeatInterval: taskData.repeat_interval,
        RepeatNeverEnds: taskData.repeat_never_ends || false,
        WeeklyDays: taskData.repeat_week_days ? taskData.repeat_week_days : [],
        MonthlyRepeatType: taskData.monthly_repeat_type || 'day',
        assigneeList: taskData.assignee_list,
        Mentions: enrichedMentionList,
        Observers: taskData.observers || [],
        Status: taskData.status,
        Files: (taskData.files),
        Checklist: updatedChecklist,
        delta: taskData.delta,
        IsRepeatable: taskData?.is_repeatable,
        repeated_from_task_id: taskData?.repeated_from_task_id ?? null,
        SendWarning: taskData.email_reminder,
        AllowAssigneeUpdate: taskData.is_assignee_update_allowed,
        ProjectId: taskData.project_id
      };

      setInitialData(formattedData);
      // Identify repeat task context for the edit page indicator
      if (taskData?.is_repeatable && !taskData?.repeated_from_task_id) {
        setRepeatTaskContext('series');
      } else if (taskData?.repeated_from_task_id) {
        setRepeatTaskContext('instance');
      } else {
        setRepeatTaskContext(null);
      }
    } catch (err) {
      console.error("Load all data error:", err);
      
      // Handle 403 Forbidden error
      if (err.response && err.response.status === 409) {
        toast.error("Access denied. You don't have permission to view this task.");
        // Navigate back to task list after a short delay
        setTimeout(() => {
          navigate('/task/tasklist');
        }, 2000);
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = formatDateForInput;

  const formatDateWithSuffix = (dateString) => {
    return formatDateToDDMMMYYYY(dateString);
  };

  const handleSubmit = async (values, setSubmitting) => {
    const formData = new FormData();
    const numericStatus = values.Status ?? 0;

    if (userdata) {
      // Append scalar fields
      const safeAppend = (key, value) => {
        if (
          value !== undefined &&
          value !== null &&
          value !== '' &&
          !(Array.isArray(value) && value.length === 0)
        ) {
          formData.append(key, value);
        }
      };

      safeAppend('TaskId', taskid);
      safeAppend('TaskName', values.TaskName);
      safeAppend('ProjectId', values.ProjectId);
      safeAppend('Description', values.Description);
      // Only append Delta if it exists and is not null
      if (values.Delta) {
        safeAppend('Delta', JSON.stringify(values.Delta));
      }
      safeAppend('Priority', values.Priority);
      safeAppend('Status', numericStatus);
      
      const applySameDayTimes = taskUsesSameDayTimes(values.StartDate, values.Deadline);
      safeAppend('StartDate', buildTaskDatetime(values.StartDate, values.StartTime, applySameDayTimes));
      safeAppend('EndDate', buildTaskDatetime(values.Deadline, values.EndTime, applySameDayTimes));
      safeAppend('RepeatType', values.RepeatType);
      safeAppend('RepeatInterval', values.RepeatInterval);
      safeAppend('RepeatUntil', values.RepeatUntil);
      safeAppend('RepeatNeverEnds', values.RepeatNeverEnds || false);
      safeAppend('RepeatWeekDays', values.WeeklyDays ? JSON.stringify(values.WeeklyDays) : null);
      safeAppend('MonthlyRepeatType', values.MonthlyRepeatType || null);
      safeAppend('AllowAssigneeUpdate', values.is_assignee_update_allowed);
      safeAppend('SendWarning', values.email_reminder);
      safeAppend('CreatedBy', userdata?.id);
      safeAppend('CreatedName', userdata?.name);
      safeAppend('ReminderEmail', userdata?.email);
      safeAppend('TaskStatusEmail', userdata?.email);
      safeAppend('Email', userdata?.email);
      safeAppend('IsRepeatable', values.IsRepeatable ? 1 : 0)
      safeAppend('CorporateId', userdata.corporateId)
      safeAppend('CreateAsIndividual', values.CreateAsIndividual || false);

      // Always send org assignment fields (including empty) so removals persist on the backend.
      // safeAppend skips empty arrays, which previously left BranchId/DepartmentId/SubDepartmentId unset
      // and caused updateTask to skip clearing task_branches / task_departments / task_sub_departments.
      const toIdCsv = (value) => {
        const ids = Array.isArray(value)
          ? value
          : value === null || value === undefined || value === ''
            ? []
            : [value];
        return ids
          .map((id) => (id === null || id === undefined ? '' : String(id).trim()))
          .filter(Boolean)
          .join(',');
      };
      formData.append('BranchId', toIdCsv(values.BranchId));
      formData.append('DepartmentId', toIdCsv(values.DepartmentId));
      formData.append('SubDepartmentId', toIdCsv(values.SubDepartmentId));

      // JSON-encoded arrays/objects — always send so clearing assignees/observers/mentions works
      const mentionResult = (values.Mentions || []).map(item1 => {
        const match = availableAssignees.find(item2 => item2.CorporateUserId === parseInt(item1.id));
        return match || null;
      }).filter(Boolean);
      formData.append('Mentions', JSON.stringify(mentionResult));

      if (values.Tags?.length) {
        safeAppend('Tags', JSON.stringify(values.Tags));
      }

      formData.append('Assignees', JSON.stringify(values.assigneeList || []));

      if (values.Participants?.length) {
        safeAppend('Participants', JSON.stringify(values.Participants));
      }

      formData.append('Observers', JSON.stringify(values.Observers || []));

      const filteredChecklist = values.Checklist.filter(
        item => item.value && item.value.trim() !== ""
      );
      if (filteredChecklist?.length) {
        safeAppend('ChecklistJSON', JSON.stringify(values.Checklist));
      }

      if (values.reminderinternalmessage) {
        safeAppend('ReminderInternal', values.reminderinternalmessage);
      }

      if (values.reminderthrougthemail) {
        safeAppend("ReminderEmailFlag", values.reminderthrougthemail);
      }

      if (values.MarkHighPriority) {
        safeAppend("HighPriority", values.MarkHighPriority);
      }

      if (values.TaskPin) {
        safeAppend("PinTask", values.TaskPin);
      }

      // Append files
      if (values.Files && values.Files.length > 0) {
        let filesArray = await normalizeFilesAndStore(values.Files);
        filesArray.forEach(file => {
          formData.append('Files', file);
        });
      }

      if (values.ParentTaskId != '') {
        formData.append("ParentTaskId", values.ParentTaskId);
      } else {
        formData.append("ParentTaskId", null);
      }

      try {
        const response = await axiosInstance.put('/task/updatetask', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        const result = response.data;
        if (result && result.success === false) {
          toast.error(result.error || result.message || 'Failed to update task');
          setSubmitting(false);
        } else if (response.status === 200) {
          toast.success("Task updated successfully");
          setIsRedirecting(true);
          
          // Add 2-second delay before redirecting
          setTimeout(() => {
            setSubmitting(false);
            // After editing a repeat task series, always go to task list; otherwise go back
            if (repeatTaskContext === 'series') {
              const search = searchParams.get('search') || '';
              navigate(`/task/tasklist${search ? `?search=${encodeURIComponent(search)}` : ''}`);
            } else {
              navigateBackSafely();
            }
          }, 2000);
        } else {
          toast.error(result?.message || 'Failed to update task');
          setSubmitting(false);
        }
      } catch (error) {
        console.error('Error during API call:', error);
        toast.error(error.response?.data?.error || error.response?.data?.message || error.message || 'Error updating task');
        setSubmitting(false);
      }
    }
  };

  const normalizeFilesAndStore = async (files) => {
    const finalFilesArray = await Promise.all(
      files.map(async (file) => {
        if (file instanceof File) return file;

        const response = await axiosInstance.get(`/task/attachments/${file.id}`, {
          responseType: 'blob'
        });
        const blob = response.data;

        const getMimeType = (extension) => {
          const mimeMap = {
            png: 'image/png',
            jpg: 'image/jpeg',
            jpeg: 'image/jpeg',
            pdf: 'application/pdf',
            doc: 'application/msword',
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            txt: 'text/plain',
          };
          return mimeMap[extension.toLowerCase()] || 'application/octet-stream';
        };

        const mimeType = getMimeType(file.file_extension);
        return new File([blob], file.file_name, { type: mimeType });
      })
    );

    return finalFilesArray;
  };

  const handleCancel = () => {
    navigateBackSafely();
  };

  if (loading || !initialData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button
        className="back-button"
        onClick={() => {
          navigateBackSafely();
        }}
      >
        <FiArrowLeft style={{ marginRight: '0.5rem' }} />
        Back
      </button>
      <TaskForm
        mode="edit"
        initialData={initialData}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        availableAssignees={availableAssignees}
        Tags={Tags}
        observers={observers}
        participants={participants}
        projects={projects}
        canSelectProject={canAccessProjects}
        Tasks={Tasks}
        Mentions={Mentions}
        delta={delta}
        Description={Description}
        userdata={userdata}
        isRedirecting={isRedirecting}
        branches={branches}
        departments={departments}
        subDepartments={subDepartments}
      />
      <InternalFooter />
    </div>
  );
};

export default EditTask; 